import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { LogoMark } from "@/components/Logo";

const Shell = ({ children, testid }) => (
  <main className="flex min-h-screen items-center justify-center px-5 pt-24" data-testid={testid}>
    <div className="w-full max-w-sm"><LogoMark size={44} />{children}</div>
  </main>
);

export function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr("");
    try {
      await api.post("/auth/forgot-password", { email, origin_url: window.location.origin });
      setSent(true);
    } catch (e2) { setErr(errMsg(e2)); } finally { setBusy(false); }
  };

  return (
    <Shell testid="forgot-password-page">
      <h1 className="mt-8 font-display text-4xl font-semibold tracking-tight">Forgot password</h1>
      {sent ? (
        <p className="mt-4 text-slate-300" data-testid="forgot-sent">If an account exists for {email}, a reset link is on its way. It expires in 1 hour.</p>
      ) : (
        <form onSubmit={submit}>
          <p className="mt-2 text-sm text-slate-400">Enter your account email and we'll send a reset link.</p>
          <input className="field mt-8" type="email" required placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="forgot-email-input" />
          {err && <p className="mt-4 text-sm text-rose-400" data-testid="forgot-error">{err}</p>}
          <button disabled={busy} className="btn-primary mt-6 w-full justify-center" data-testid="forgot-submit-button">{busy && <Loader2 className="h-4 w-4 animate-spin" />} Send reset link</button>
        </form>
      )}
      <Link to="/login" className="mt-8 inline-block text-sm text-slate-300 hover:text-white" data-testid="forgot-back-login">Back to sign in</Link>
    </Shell>
  );
}

export function ResetPassword() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (pw !== pw2) { setErr("Passwords do not match"); return; }
    setBusy(true);
    setErr("");
    try {
      await api.post("/auth/reset-password", { token: params.get("token") || "", password: pw });
      toast.success("Password updated. Sign in with your new password.");
      nav("/login");
    } catch (e2) { setErr(errMsg(e2)); } finally { setBusy(false); }
  };

  return (
    <Shell testid="reset-password-page">
      <h1 className="mt-8 font-display text-4xl font-semibold tracking-tight">Choose a new password</h1>
      <form onSubmit={submit} className="mt-8 space-y-4">
        <input className="field" type="password" required minLength={8} autoComplete="new-password" placeholder="New password (8+ characters)" value={pw} onChange={(e) => setPw(e.target.value)} data-testid="reset-password-input" />
        <input className="field" type="password" required minLength={8} autoComplete="new-password" placeholder="Confirm new password" value={pw2} onChange={(e) => setPw2(e.target.value)} data-testid="reset-password-confirm-input" />
        {err && <p className="text-sm text-rose-400" data-testid="reset-error">{err}</p>}
        <button disabled={busy} className="btn-primary w-full justify-center" data-testid="reset-submit-button">{busy && <Loader2 className="h-4 w-4 animate-spin" />} Update password</button>
      </form>
    </Shell>
  );
}

export function Account() {
  const { user, logout } = useAuth();
  const [cur, setCur] = useState("");
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const isGoogle = user?.auth_provider === "google";

  const submit = async (e) => {
    e.preventDefault();
    if (pw !== pw2) { setErr("New passwords do not match"); return; }
    setBusy(true);
    setErr("");
    try {
      await api.post("/auth/change-password", { current_password: cur, new_password: pw });
      toast.success("Password changed");
      setCur(""); setPw(""); setPw2("");
    } catch (e2) { setErr(errMsg(e2)); } finally { setBusy(false); }
  };

  return (
    <main className="mx-auto max-w-xl px-5 pb-24 pt-36" data-testid="account-page">
      <p className="eyebrow">Account</p>
      <h1 className="mt-4 font-display text-4xl font-semibold tracking-tight">{user?.name}</h1>
      <p className="mt-2 text-slate-300" data-testid="account-email">{user?.email} · <span className="capitalize">{user?.role}</span></p>
      <form onSubmit={submit} className="mt-10 space-y-4 border border-white/10 bg-[#0d1524] p-6" data-testid="change-password-form">
        <p className="eyebrow">Change password</p>
        {isGoogle && <p className="text-sm text-slate-400">You sign in with Google. If you never set a password, leave "Current password" empty to create one.</p>}
        <input className="field" type="password" autoComplete="current-password" placeholder="Current password" value={cur} onChange={(e) => setCur(e.target.value)} data-testid="account-current-password" />
        <input className="field" type="password" required minLength={8} autoComplete="new-password" placeholder="New password (8+ characters)" value={pw} onChange={(e) => setPw(e.target.value)} data-testid="account-new-password" />
        <input className="field" type="password" required minLength={8} autoComplete="new-password" placeholder="Confirm new password" value={pw2} onChange={(e) => setPw2(e.target.value)} data-testid="account-confirm-password" />
        {err && <p className="text-sm text-rose-400" data-testid="account-error">{err}</p>}
        <button disabled={busy} className="btn-primary" data-testid="account-change-password-button">{busy && <Loader2 className="h-4 w-4 animate-spin" />} Update password</button>
      </form>
      <div className="mt-8 flex gap-3">
        <Link to={user?.role === "admin" ? "/admin" : "/portal"} className="btn-ghost" data-testid="account-back-dashboard">Back to dashboard</Link>
        <button onClick={logout} className="btn-ghost" data-testid="account-logout">Sign out</button>
      </div>
    </main>
  );
}
